if (!customElements.get("ecomhard-kit-selector")) {
  class EcomhardKitSelector extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isSubmitting = false;
      this.slides = [];
      this.onChange = this.onChange.bind(this);
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onAddAll = this.onAddAll.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.cacheElements();
      this.createSlides();

      if (!this.slides.length) return;

      this.addEventListener("change", this.onChange);
      this.previousButton?.addEventListener("click", this.onPrevious);
      this.nextButton?.addEventListener("click", this.onNext);
      this.addAllButton?.addEventListener("click", this.onAddAll);

      this.slides.forEach((slide) => {
        this.syncAvailability(slide);
        this.updateSlideDisplay(slide);
      });

      this.goTo(0);
      this.updateTotal();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onChange);
      this.previousButton?.removeEventListener("click", this.onPrevious);
      this.nextButton?.removeEventListener("click", this.onNext);
      this.addAllButton?.removeEventListener("click", this.onAddAll);
    }

    cacheElements() {
      this.counter = this.querySelector("[data-kit-counter]");
      this.previousButton = this.querySelector("[data-kit-previous]");
      this.nextButton = this.querySelector("[data-kit-next]");
      this.addAllButton = this.querySelector("[data-kit-add-all]");
      this.totalElement = this.querySelector("[data-kit-total]");
      this.subtotalElement = this.querySelector("[data-kit-subtotal]");
      this.discountLabelElement = this.querySelector("[data-kit-discount-label]");
      this.errorElement = this.querySelector("[data-kit-error]");
      this.cartDrawer = document.querySelector("cart-drawer");
      this.mainCart = document.querySelector("cart-items");
    }

    createSlides() {
      const slideElements = Array.from(this.querySelectorAll("[data-kit-slide]"));

      this.slides = slideElements.map((element, index) => {
        const productJson = element.querySelector("[data-kit-product-json]");
        let product = {};

        try {
          product = JSON.parse(productJson?.textContent || "{}");
        } catch (error) {
          console.error("[ECOMHARD Kits] Não foi possível ler os dados do produto.", error);
        }

        const variants = Array.isArray(product.variants) ? product.variants : [];
        const inputElements = Array.from(element.querySelectorAll("[data-kit-option]"));
        const maxOptions = Number(product.options?.length || 0);
        const defaultVariant =
          maxOptions === 0
            ? variants.find((variant) => variant.available) || variants[0] || null
            : null;

        return {
          index,
          element,
          product,
          variants,
          maxOptions,
          defaultVariant,
          selections: new Map(),
          inputs: inputElements,
          priceElement: element.querySelector("[data-kit-price]"),
          comparePriceElement: element.querySelector("[data-kit-compare-price]"),
          imageElement: element.querySelector("[data-kit-image]"),
          imagePlaceholder: element.querySelector("[data-kit-image-placeholder]"),
          selectedValueElements: Array.from(
            element.querySelectorAll("[data-kit-selected-value]")
          ),
        };
      });
    }

    normalize(value) {
      return String(value ?? "").trim().toLocaleLowerCase("pt-BR");
    }

    getVariantOptionValue(variant, position) {
      return variant?.[`option${position}`] ?? "";
    }

    getSelectedVariant(slide) {
      if (slide.defaultVariant) return slide.defaultVariant;
      if (!slide.maxOptions || slide.selections.size !== slide.maxOptions) return null;

      return (
        slide.variants.find((variant) => {
          if (!variant.available) return false;

          for (let position = 1; position <= slide.maxOptions; position += 1) {
            const selectedValue = slide.selections.get(String(position));
            if (
              this.normalize(this.getVariantOptionValue(variant, position)) !==
              this.normalize(selectedValue)
            ) {
              return false;
            }
          }

          return true;
        }) || null
      );
    }

    getDisplayVariant(slide) {
      return (
        this.getSelectedVariant(slide) ||
        slide.variants.find((variant) => variant.available) ||
        slide.variants[0] ||
        null
      );
    }

    canSelectOption(slide, position, value) {
      const proposedSelection = new Map(slide.selections);
      proposedSelection.set(String(position), value);

      return slide.variants.some((variant) => {
        if (!variant.available) return false;

        for (const [selectedPosition, selectedValue] of proposedSelection.entries()) {
          if (
            this.normalize(this.getVariantOptionValue(variant, selectedPosition)) !==
            this.normalize(selectedValue)
          ) {
            return false;
          }
        }

        return true;
      });
    }

    syncAvailability(slide) {
      slide.inputs.forEach((input) => {
        const position = input.dataset.optionPosition;
        const value = input.dataset.optionValue;
        const available = this.canSelectOption(slide, position, value);
        const isSelected =
          this.normalize(slide.selections.get(String(position))) === this.normalize(value);
        const option = input.closest(".eh-kit__option");

        input.disabled = !available && !isSelected;
        option?.classList.toggle("is-unavailable", !available);
        option?.setAttribute("aria-disabled", String(!available));
      });

      slide.selectedValueElements.forEach((element) => {
        const position = String(element.dataset.kitSelectedValue);
        element.textContent = slide.selections.get(position) || "";
      });
    }

    updateSlideDisplay(slide) {
      const variant = this.getDisplayVariant(slide);
      if (!variant) return;

      if (slide.priceElement) {
        slide.priceElement.textContent = this.formatMoney(Number(variant.price || 0));
      }

      if (slide.comparePriceElement) {
        const compareAtPrice = Number(variant.compare_at_price || 0);
        const price = Number(variant.price || 0);
        const hasCompareAtPrice = compareAtPrice > price;

        slide.comparePriceElement.hidden = !hasCompareAtPrice;
        if (hasCompareAtPrice) {
          slide.comparePriceElement.textContent = this.formatMoney(compareAtPrice);
        }
      }

      const image = variant.featured_image || slide.product.featured_image;
      const imageSource = typeof image === "string" ? image : image?.src;

      if (slide.imageElement && imageSource) {
        slide.imageElement.src = imageSource;
        slide.imageElement.alt = variant.name || slide.product.title || "";
      }
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === "function") {
        return window.formatMoney(
          cents,
          window.FoxThemeSettings?.money_format || this.dataset.moneyFormat
        );
      }

      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || "BRL";

      try {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency,
        }).format(amount);
      } catch (error) {
        return `R$ ${amount.toFixed(2).replace(".", ",")}`;
      }
    }

    getAllSelectedVariants() {
      return this.slides.map((slide) => this.getSelectedVariant(slide));
    }

    getAllDisplayVariants() {
      return this.slides.map((slide) => this.getDisplayVariant(slide));
    }

    parseDiscountValue(rawValue) {
      let value = String(rawValue ?? "").trim();
      if (!value) return 0;

      value = value.replace(/[^\d,.-]/g, "");

      const commaIndex = value.lastIndexOf(",");
      const dotIndex = value.lastIndexOf(".");

      if (commaIndex > -1 && dotIndex > -1) {
        if (commaIndex > dotIndex) {
          value = value.replace(/\./g, "").replace(",", ".");
        } else {
          value = value.replace(/,/g, "");
        }
      } else if (commaIndex > -1) {
        value = value.replace(",", ".");
      }

      const parsedValue = Number.parseFloat(value);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
    }

    getDiscountConfig() {
      const type = this.normalize(this.dataset.kitDiscountType);
      const value = this.parseDiscountValue(this.dataset.kitDiscountValue);

      if (!value) return null;

      if (type.includes("porcent")) {
        return {
          type: "percentage",
          value: Math.min(value, 100),
        };
      }

      if (type.includes("fix")) {
        return {
          type: "fixed",
          value,
        };
      }

      return null;
    }

    getTotals() {
      const subtotal = this.getAllDisplayVariants().reduce(
        (sum, variant) => sum + Number(variant?.price || 0),
        0
      );
      const discount = this.getDiscountConfig();

      if (!discount || subtotal <= 0) {
        return {
          subtotal,
          discountAmount: 0,
          total: subtotal,
          discount,
        };
      }

      const rawDiscountAmount =
        discount.type === "percentage"
          ? Math.round((subtotal * discount.value) / 100)
          : Math.round(discount.value * 100);
      const discountAmount = Math.min(Math.max(rawDiscountAmount, 0), subtotal);

      return {
        subtotal,
        discountAmount,
        total: Math.max(subtotal - discountAmount, 0),
        discount,
      };
    }

    formatPercentage(value) {
      try {
        return new Intl.NumberFormat("pt-BR", {
          maximumFractionDigits: 2,
        }).format(Number(value || 0));
      } catch (error) {
        return String(value || 0).replace(".", ",");
      }
    }

    updateTotal() {
      if (!this.totalElement) return;

      const { subtotal, discountAmount, total, discount } = this.getTotals();

      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(subtotal);
      }

      this.totalElement.textContent = this.formatMoney(total);

      if (this.discountLabelElement) {
        if (discount && discountAmount > 0) {
          this.discountLabelElement.hidden = false;
          this.discountLabelElement.textContent =
            discount.type === "percentage"
              ? `-${this.formatPercentage(discount.value)}%`
              : `-${this.formatMoney(discountAmount)}`;
        } else {
          this.discountLabelElement.hidden = true;
          this.discountLabelElement.textContent = "";
        }
      }
    }

    updateNavigation() {
      const currentSlide = this.slides[this.currentIndex];
      const currentVariant = this.getSelectedVariant(currentSlide);
      const isFirst = this.currentIndex === 0;
      const isLast = this.currentIndex === this.slides.length - 1;
      const canAddAll = this.getAllSelectedVariants().every(Boolean);

      if (this.counter) {
        this.counter.textContent = `Item ${this.currentIndex + 1} de ${this.slides.length}`;
      }

      if (this.previousButton) {
        this.previousButton.hidden = isFirst;
      }

      if (this.nextButton) {
        this.nextButton.hidden = isLast;
        this.nextButton.disabled = !currentVariant || this.isSubmitting;
      }

      if (this.addAllButton) {
        this.addAllButton.hidden = !isLast;
        this.addAllButton.disabled = !canAddAll || this.isSubmitting;
      }
    }

    goTo(index) {
      if (index < 0 || index >= this.slides.length) return;

      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const isCurrent = slideIndex === index;
        slide.element.hidden = !isCurrent;
        slide.element.setAttribute("aria-hidden", String(!isCurrent));
      });

      this.clearError();
      this.updateNavigation();

      this.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    onChange(event) {
      const input = event.target.closest("[data-kit-option]");
      if (!input || input.disabled) return;

      const slideElement = input.closest("[data-kit-slide]");
      const slide = this.slides.find((item) => item.element === slideElement);
      if (!slide) return;

      slide.selections.set(String(input.dataset.optionPosition), input.dataset.optionValue);
      this.syncAvailability(slide);
      this.updateSlideDisplay(slide);
      this.updateTotal();
      this.updateNavigation();
      this.clearError();
    }

    onPrevious() {
      this.goTo(this.currentIndex - 1);
    }

    onNext() {
      const currentVariant = this.getSelectedVariant(this.slides[this.currentIndex]);
      if (!currentVariant) return;
      this.goTo(this.currentIndex + 1);
    }

    getSectionsToRender() {
      let sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", {
          bubbles: true,
          detail: { sections },
        })
      );

      if (this.cartDrawer?.getSectionsToRender) {
        sections = [
          ...sections,
          ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
        ];
      }

      if (this.mainCart?.getSectionsToRender) {
        sections = [
          ...sections,
          ...this.mainCart.getSectionsToRender().map((section) => section.section),
        ];
      }

      return [...new Set(sections.filter(Boolean))];
    }

    async onAddAll() {
      const variants = this.getAllSelectedVariants();
      if (variants.some((variant) => !variant) || this.isSubmitting) return;

      this.setLoading(true);
      this.clearError();

      const sections = this.getSectionsToRender();
      const payload = {
        items: variants.map((variant) => ({
          id: Number(variant.id),
          quantity: 1,
        })),
        sections: sections.join(","),
        sections_url: window.location.pathname,
      };

      try {
        const response = await fetch(
          this.dataset.cartAddUrl || window.FoxThemeSettings?.routes?.cart_add_url || "/cart/add.js",
          {
            method: "POST",
            headers: {
              Accept: "application/javascript",
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(payload),
          }
        );

        const state = await response.json();

        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || "Não foi possível adicionar os produtos ao carrinho.");
        }

        if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== "undefined") {
          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, state);
        }

        document.dispatchEvent(
          new CustomEvent("product-ajax:added", {
            detail: { product: state, source: "ecomhard-kits" },
          })
        );

        if (!this.cartDrawer && !this.mainCart && window.FoxThemeCartHelpers?.getState) {
          const cart = await window.FoxThemeCartHelpers.getState();
          window.FoxThemeCartHelpers.updateCartCount?.(cart.item_count);
        }
      } catch (error) {
        console.error("[ECOMHARD Kits]", error);
        this.showError(error.message || "Não foi possível adicionar os produtos ao carrinho.");
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.classList.toggle("is-loading", isLoading);
      this.updateNavigation();
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.hidden = false;
      this.errorElement.textContent = message;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
      this.errorElement.textContent = "";
    }
  }

  customElements.define("ecomhard-kit-selector", EcomhardKitSelector);
}
